import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  // ...existing code...
} from "@mui/material";
import { FormGrid } from "../FormGrid";
import {
  nextStep,
  changeData,
  useSteps,
} from "../../shared/features/subscription/stepsSlice";
import { useDispatch, useSelector } from "react-redux";
import { IFormQuery, SubscribeData } from "shared/interfaces";
import { estadosBrasileiros } from "shared/utils/states";
import { stepOneSchema } from "shared/schemas/subscription";
import InputMask from "react-input-mask";
import { FormTitleSession } from "components/FormTitleSession";

const DDD_VALIDOS = [
  '11','12','13','14','15','16','17','18','19',
  '21','22','24',
  '27','28',
  '31','32','33','34','35','37','38',
  '41','42','43','44','45','46',
  '47','48','49',
  '51','53','54','55',
  '61',
  '62','64',
  '63',
  '65','66',
  '67',
  '68',
  '69',
  '71','73','74','75','77',
  '79',
  '81','87',
  '82',
  '83',
  '84',
  '85','88',
  '86','89',
  '91','93','94',
  '92','97',
  '95',
  '96',
  '98','99'
];


export const StepOne: React.FC = () => {
  const [dddError, setDddError] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const sessionDefBool = sessionStorage.getItem("deficienciaBoolean");
  const sessionDefText = sessionStorage.getItem("deficiencia");
  // Sincroniza o valor do select visual com o valor booleano
  // deficienciaBoolean: boolean | TBoolean
  // Estado local para refletir o valor do select
  const [deficiencia, setDeficiencia] = useState(
    (() => {
      const sessionData = sessionStorage.getItem("stepOneFormData");
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        if (typeof parsed.deficienciaBoolean !== "undefined") {
          return parsed.deficienciaBoolean === true || parsed.deficienciaBoolean === "T";
        }
      }
      return sessionDefBool === "true";
    })()
  );
  const [deficienciaTexto, setDeficienciaTexto] = useState(sessionDefText || "");
  const { data: formData } = useSelector(useSteps);
  const dispatch = useDispatch();

  const saveToSession = (data: Partial<SubscribeData>) => {
    const allowedFields = [
      "nome", "email", "confirmEmail", "cpf", "telefone", "dataNascimento", "cidade", "estado", "genero", "deficienciaBoolean", "deficiencia"
    ];
    const filtered = Object.fromEntries(Object.entries(data).filter(([key]) => allowedFields.includes(key)));
    sessionStorage.setItem("stepOneFormData", JSON.stringify(filtered));
  };

  const getSessionData = (): Partial<SubscribeData> => {
    const raw = sessionStorage.getItem("stepOneFormData");
    return raw ? JSON.parse(raw) : {};
  };

  const initialData = {
    deficienciaBoolean:
      sessionDefBool === null || sessionDefBool === "" ? false : (sessionDefBool === "true" ? true : false),
    deficiencia: sessionDefText || "",
    genero:
      (formData?.genero ?? getSessionData().genero ?? "MASCULINO"),
    estado:
      (formData?.estado ?? getSessionData().estado ?? "AC"),
    ...getSessionData(),
    ...formData
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SubscribeData>({
    resolver: yupResolver(stepOneSchema),
    defaultValues: initialData,
  });

  useEffect(() => {
    const subscription = watch((value) => {
      const fixedValue = {
        ...value,
        linguagens: Array.isArray(value.linguagens)
          ? (value.linguagens.filter((l): l is string => typeof l === 'string'))
          : value.linguagens,
      };
      saveToSession(fixedValue);
    });
    return () => subscription.unsubscribe();
  }, [watch]);


  useEffect(() => {
    if (formData?.confirmEmail) {
      setValue("confirmEmail", formData.confirmEmail);
    }
    if (formData?.dataNascimento) {
      const data = new Date(formData.dataNascimento);
      if (!isNaN(data.getTime())) {
        const yyyy = data.getFullYear();
        const mm = String(data.getMonth() + 1).padStart(2, '0');
        const dd = String(data.getDate()).padStart(2, '0');
        setValue("dataNascimento", `${yyyy}-${mm}-${dd}`);
      }
    }
  }, [formData, setValue]);

  const onSubmit = (data: SubscribeData) => {
    const telefone = data.telefone || "";
    const ddd = telefone.replace(/[^\d]/g, '').substring(0, 2);
    setDddError(null);
    if (!DDD_VALIDOS.includes(ddd)) {
      setDddError("DDD inválido, corrija o campo de telefone.");
      return;
    }

    data.neurodiversidadeBoolean === "F" &&
      (data.neurodiversidade = "Não possui");

    data.deficienciaBoolean === "F" && 
      (data.deficiencia = "Não possui");
    dispatch(nextStep());
    dispatch(changeData(data));
    // Salva no sessionStorage ao avançar
    saveToSession(data);
  };

  const FormName: React.FC<IFormQuery> = ({ nome }) => {
    return <>{nome ? nome : <CircularProgress size={22} />}</>;
  };

  const helperWithCount = (
    value: string | undefined,
    max: number,
    error?: string,
    fallback?: string,
    fieldName?: string
  ) => {
    const count = (value ?? "").length;
    const message = error ?? fallback;
    const showCount = focusedField === fieldName;

    if (!message && !showCount) return undefined;

    return (
      <Box
        component="span"
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box component="span">{message ?? ""}</Box>
        {showCount && (
          <Box component="span" sx={{ ml: "auto", textAlign: "right" }}>
            {`${count}/${max}`}
          </Box>
        )}
      </Box>
    );
  };

  const nomeValue = watch("nome") ?? "";
  const emailValue = watch("email") ?? "";
  const confirmEmailValue = watch("confirmEmail") ?? "";
  const cidadeValue = watch("cidade") ?? "";
  const deficienciaValue = watch("deficiencia") ?? "";
  const deficienciaField = register("deficiencia");

  // Handler genérico para persistir selects no sessionStorage
  const handleSelectChange = (field: keyof SubscribeData) => (event: any) => {
    const value = event.target.value;
    setValue(field, value);
    const current = watch();
    const updated = { ...current, [field]: value };
    if (field === "deficienciaBoolean") {
      setDeficiencia(value === true || value === "T");
    }
    saveToSession(updated);
  };

  return (
    <FormGrid onSubmit={handleSubmit(onSubmit)}>
      <FormTitleSession title={"Informações Pessoais"} />
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={<FormName nome={"Digite seu nome completo *"} />}
          defaultValue={formData?.nome}
          error={!!errors.nome}
          helperText={helperWithCount(nomeValue, 100, errors.nome?.message, undefined, "nome")}
          inputProps={{ maxLength: 100 }}
          id="step-1-nome"
          {...register("nome")}
          onFocus={() => setFocusedField("nome")}
          onBlur={(e) => {
              e.target.value = e.target.value.trimStart();
              setFocusedField(null);
          }}
        />
      </Grid>
      <Grid item xs={smDown ? 12 : 6}>
        <Tooltip
          title="Não é permitido e-mail universitário ou corporativo. Utilize um e-mail pessoal válido e que você tenha acesso durante todas etapas."
          placement="top"
          arrow
        >
          <TextField
            fullWidth
            label={<FormName nome={"Digite seu email *"} />}
            defaultValue={formData?.email}
            error={!!errors.email}
            helperText={helperWithCount(
              emailValue,
              60,
              errors.email?.message,
              "Utilize um e-mail pessoal.",
              "email"
            )}
            inputProps={{ maxLength: 60 }}
            id="step-1-email"
            {...register("email")}
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField(null)}
          />
        </Tooltip>
      </Grid>
      <Grid item xs={smDown ? 12 : 6}>
        <Tooltip
          title="Não é permitido e-mail universitário ou corporativo. Utilize um e-mail pessoal válido e que você tenha acesso durante todas etapas."
          placement="top"
          arrow
        >
          <TextField
            fullWidth
            label={<FormName nome={"Confirme seu e-mail *"} />}
            error={!!errors.confirmEmail}
            helperText={helperWithCount(
              confirmEmailValue,
              60,
              errors.confirmEmail?.message,
              "Utilize um e-mail pessoal.",
              "confirmEmail"
            )}
            id="step-1-email-confirmation"
            inputProps={{ maxLength: 60 }}
            onPaste={(e) => e.preventDefault()}
            {...register("confirmEmail")}
            defaultValue={formData?.confirmEmail}
            onFocus={() => setFocusedField("confirmEmail")}
            onBlur={() => setFocusedField(null)}
          />
        </Tooltip>
      </Grid>
      <Grid item xs={smDown ? 12 : 6}>
        <InputMask
          mask="999.999.999-99"
          maskChar=" "
          {...register("cpf")}
          defaultValue={formData?.cpf}
        >
          {
            // @ts-ignore
            (inputProps) => (
              <TextField
                {...inputProps}
                label={<FormName nome={"Digite seu CPF *"} />}
                variant="outlined"
                id="step-1-cpf"
                helperText={errors.cpf?.message}
                error={!!errors.cpf}
                sx={{
                  width: "100%",
                }}
              />
            )
          }
        </InputMask>
      </Grid>
      <Grid
        item
        xs={smDown ? 12 : 6}
        display="flex"
        flexDirection="column"
        onFocusCapture={e => {
          if (e.target.id === 'step-1-telefone') setDddError(null);
        }}
      >
        <InputMask
          mask="(99)99999-9999"
          maskChar=""
          {...register("telefone")}
          defaultValue={formData?.telefone}
        >
          {
            (inputProps) => {
              const { value, defaultValue, onFocus, ...restInputProps } = inputProps;
              return (
                <TextField
                  {...restInputProps}
                  label={<FormName nome={"Digite seu telefone celular *"} />}
                  variant="outlined"
                  error={!!errors.telefone || !!dddError}
                  helperText={
                    errors.telefone?.message
                      ? errors.telefone?.message
                      : dddError
                  }
                  id="step-1-telefone"
                />
              );
            }
          }
        </InputMask>
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          label={<FormName nome={"Digite sua data de nascimento *"} />}
          type="date"
          error={!!errors.dataNascimento}
          helperText={errors.dataNascimento?.message}
          id="step-1-dataNascimento"
          InputLabelProps={{ shrink: true }}
          {...register("dataNascimento")}
          defaultValue={formData?.dataNascimento ? (() => {
            const data = new Date(formData.dataNascimento);
            if (!isNaN(data.getTime())) {
              const yyyy = data.getFullYear();
              const mm = String(data.getMonth() + 1).padStart(2, '0');
              const dd = String(data.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            }
            return '';
          })() : ''}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth>
          <InputLabel id="label-genero-candidato" sx={{ color: "#525252" }}>
            <FormName nome={"Genero*"} />
          </InputLabel>
          <Select
            id="s2-select-genero-candidato"
            label={<FormName nome={"Genero*"} />}
            fullWidth
            value={watch("genero") ?? "MASCULINO"}
            onChange={handleSelectChange("genero")}
          >
            <MenuItem value="MASCULINO">Masculino</MenuItem>
            <MenuItem value="FEMININO">Feminino</MenuItem>
            <MenuItem value="PREFIRO_NAO_INFORMAR">Prefiro não informar</MenuItem>
            <MenuItem value="OUTRO">Outro</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <FormControl fullWidth>
          <InputLabel>Estado *</InputLabel>
          <Select
            label={<FormName nome={"Estado *"} />}
            error={!!errors.estado}
            id="step-1-estado"
            value={watch("estado") ?? "AC"}
            onChange={handleSelectChange("estado")}
          >
            {estadosBrasileiros.map((estado) => (
              <MenuItem key={estado.id} value={estado.sigla}>
                {estado.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          label={<FormName nome={"Cidade *"} />}
          error={!!errors.cidade}
          helperText={helperWithCount(cidadeValue, 60, errors.cidade?.message, undefined, "cidade")}
          id="step-1-cidade"
          inputProps={{ maxLength: 60 }}
          defaultValue={formData?.cidade}
          {...register("cidade", {
              setValueAs: (v) =>
                  typeof v === "string" ? v.replace(/\s+/g, " ").trim() : v,
          })}
          onFocus={() => setFocusedField("cidade")}
          onBlur={() => setFocusedField(null)}
        />
      </Grid>
      {/* <Grid item xs={12}>
        <Tooltip title={"Você possui alguma neurodiversidade? obs: Neurodiversidade é o funcionamento neurocognitivo distinto ou atípico, que podem influênciar nas relações sociais e educativas."} placement="top" arrow>
          <FormControl fullWidth>
            <InputLabel>
              {<FormName nome={"Neurodiversidade * obs: Neurodiversidade é o funcionamento neurocognitivo distinto ou atípico, que podem influênciar nas relações sociais e educativas."} />}
            </InputLabel>
            <Select
              label={<FormName nome={"Neurodiversidade * obs: Neurodiversidade é o funcionamento neurocognitivo distinto ou atípico, que podem influênciar nas relações sociais e educativas."} />}
              defaultValue={
                formData?.neurodiversidadeBoolean
                  ? formData.neurodiversidadeBoolean
                  : "F"
              }
              id="step-1-neurodiversidade-combo"
              {...register("neurodiversidadeBoolean")}
              onChange={(event) => {
                setNeurodiversidade(event.target.value === "T" ? "T" : "F");
              }}
            >
              <MenuItem value="F">Não</MenuItem>
              <MenuItem value="T">Sim</MenuItem>
            </Select>
          </FormControl>
        </Tooltip>
      </Grid>
      {neurodiversidade === "T" && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Qual neurodiversidade você possui?"
            error={!!errors.neurodiversidade}
            helperText={errors.neurodiversidade?.message}
            inputProps={{ maxLength: 255 }}
            id="step-1-neurodiversidade"
            requiredRROR in src/components/Steps/StepOne.tsx:171:22
TS2345: Argument of type '"T" | "F"' is not assignable to parameter of type 'SetStateAction<boolean>'.
  Type '"T"' is not assignable to type 'SetStateAction<boolean>'.
    169 |     const updated = { ...current, [field]: value };
    170 |     if (field === "deficienciaBoolean") {
  > 171 |       setDeficiencia(value === true || value === "T" ? "T" : "F");
        |                      ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
    172 |       sessionStorage.setItem("deficienciaBoolean", value === true || value === "T" ? "true" : "false");
    173 |     }
    174 |     saveToSession(updated);

ERROR in src/components/Steps/StepOne.tsx:429:10
TS2367: This comparison appears to be unintentional because the types 'boolean' and 'string' have no overlap.
    427 |         </Tooltip>
    428 |       </Grid>
  > 429 |         {deficiencia === "T" && (
        |          ^^^^^^^^^^^^^^^^^^^
    430 |           <Grid item xs={12}>
    431 |             <TextField
    432 |               label={<FormName nome={"Qual deficiência você possui?"} />}


            defaultValue={formData?.neurodiversidade}
            {...register("neurodiversidade")}
          />
        </Grid>
      )} */}
      <Grid item xs={12}>
        <Tooltip
          title="Essa pergunta é importante para que possamos oferecer uma melhor experiência para você."
          placement="top"
          arrow
        >
          <FormControl fullWidth>
            <InputLabel sx={{ color: "#525252" }}>
              <FormName nome={"Você possui alguma deficiência? *"} />
            </InputLabel>
            <Select
              id="s2-select-deficiencia-candidato"
              label={<FormName nome={"Você possui alguma deficiência? *"} />}
              value={deficiencia ? "T" : "F"}
              onChange={e => {
                const value = e.target.value;
                setDeficiencia(value === "T");
                setValue("deficienciaBoolean", value === "T" ? true : false);
                const current = watch();
                const updated = { ...current, deficienciaBoolean: value === "T" ? true : false };
                saveToSession(updated);
              }}
            >
              <MenuItem value="F">Não</MenuItem>
              <MenuItem value="T">Sim</MenuItem>
            </Select>
          </FormControl>
        </Tooltip>
      </Grid>
  {deficiencia === true && (
          <Grid item xs={12}>
            <TextField
              label={<FormName nome={"Qual deficiência você possui?"} />}
              variant="outlined"
              sx={{ width: "100%" }}
              defaultValue={deficienciaTexto}
              id="s2-candidato-deficiencia-descricao"
              inputProps={{ maxLength: 100 }}
              error={!!errors.deficiencia}
              helperText={helperWithCount(deficienciaValue, 100, errors.deficiencia?.message, undefined, "deficiencia")}
              required
              {...deficienciaField}
              onFocus={() => setFocusedField("deficiencia")}
              onBlur={(e) => {
                deficienciaField.onBlur(e);
                setFocusedField(null);
              }}
              onChange={e => {
                deficienciaField.onChange(e);
                setDeficienciaTexto(e.target.value);
              }}
            />
          </Grid>
        )}
      <Grid item xs={12} marginTop={4}>
        <Button type="submit" variant="contained" id="step-1-enviar">
          Próximo
        </Button>
      </Grid>
    </FormGrid>
  );
};
